import React, { useState, useEffect, useCallback } from 'react';
import { Button, Tile, Loading, InlineLoading, Modal, Accordion, AccordionItem, ComposedModal, ModalHeader, ModalBody, ModalFooter, Tooltip, InlineNotification } from '@carbon/react';
import { Renew, Error, Checkmark, Information, Locked, ArrowUp } from '@carbon/icons-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useAppData } from '../../context/AppDataContext';
import { supabase } from '../../services/supabaseClient';
import PricingTier from '../../components/pricing/PricingTier';
import { useAudUsdFxRate } from '../../hooks/useFxRate';

const SubscriptionPage = () => {
  const { theme } = useTheme();
  const { user, session } = useAuth();
  const { refreshSubscription: refreshGlobalSub } = useAppData();
  const { rate: audUsdRate } = useAudUsdFxRate();
  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';
  const successColor = '#24a148';
  const warningColor = '#f1c21b';
  const errorColor = '#da1e28';

  const formatUsdApprox = (audAmount, { maximumFractionDigits = 0 } = {}) => {
    if (typeof audUsdRate !== 'number' || !Number.isFinite(audUsdRate)) return null;
    if (typeof audAmount !== 'number' || !Number.isFinite(audAmount)) return null;
    const usd = audAmount * audUsdRate;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits,
    }).format(usd);
  };

  const usdParen = (audAmount, opts) => {
    const usd = formatUsdApprox(audAmount, opts);
    return usd ? ` (${usd} USD)` : '';
  };

  const [billingInterval, setBillingInterval] = useState('monthly');
  const isYearly = billingInterval === 'yearly';
  const yearlyDiscount = 0.20;
  const getPrice = (monthlyPrice) => {
    if (isYearly) {
      return Math.round(monthlyPrice * 12 * (1 - yearlyDiscount));
    }
    return monthlyPrice;
  };

  const pricingTiers = {
    free: {
      name: "Free",
      free: true,
      basePrice: 0,
      billingInterval,
      currency: "AUD",
      sensorCapacity: 1,
      gatewayCount: 1,
      gatewayLabel: "1 gateway",
      description: "Start monitoring your cold chain at no cost",
      features: [
        "Connect 1 sensor",
        "1 gateway",
        "Mobile app access",
        "Real-time temperature alerts",
        "30-day data history",
        "Email support"
      ],
      cta: "Get Free to Start",
      popular: false
    },
    starter: {
      name: "Starter",
      basePrice: isYearly ? getPrice(29) : 29,
      monthlyEquivalent: isYearly ? Math.round(29 * (1 - yearlyDiscount)) : null,
      billingInterval,
      currency: "AUD",
      sensorCapacity: 3,
      gatewayCount: 1,
      gatewayLabel: "1 gateway",
      description: "Perfect for local butchers and small operations",
      features: [
        "Connect up to 3 sensors",
        `Add more sensors for $5 AUD / sensor / month${usdParen(5)}`,
        "1 gateway",
        "Mobile app access",
        "Real-time temperature alerts",
        "30-day data history",
        "Email support"
      ],
      cta: "Get Started",
      popular: false
    },
    business: {
      name: "Business",
      basePrice: isYearly ? getPrice(75) : 75,
      monthlyEquivalent: isYearly ? Math.round(75 * (1 - yearlyDiscount)) : null,
      billingInterval,
      currency: "AUD",
      sensorCapacity: 10,
      gatewayCount: 3,
      gatewayLabel: "3 gateways maximum",
      description: "Ideal for abattoirs and cold storage facilities",
      features: [
        "Connect up to 10 sensors",
        `Add more sensors for $5 AUD / sensor / month${usdParen(5)}`,
        "3 gateways maximum",
        "Automated compliance reports (PDFs)",
        "1-year data history",
        "Multi-device support",
        "SMS alerts (50 included)",
        "Priority email support"
      ],
      cta: "Get Started",
      popular: true
    },
    enterprise: {
      name: "Enterprise",
      basePrice: isYearly ? getPrice(149) : 149,
      monthlyEquivalent: isYearly ? Math.round(149 * (1 - yearlyDiscount)) : null,
      billingInterval,
      currency: "AUD",
      sensorCapacity: "25+",
      gatewayCount: "10+",
      gatewayLabel: "10+ gateways",
      description: "For supply chain and large-scale logistics",
      features: [
        "Connect 25+ sensors and 10+ gateways",
        `Add more sensors for $5 AUD / sensor / month${usdParen(5)}`,
        "Multi-site management",
        "API access & integrations",
        "Custom user permissions",
        "Unlimited SMS alerts",
        "Phone & priority support",
        "Custom reporting"
      ],
      cta: "Contact Sales",
      popular: false
    }
  };

  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [sensorQuantity, setSensorQuantity] = useState(1);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [addOnModalOpen, setAddOnModalOpen] = useState(false);
  const [selectedAddOn, setSelectedAddOn] = useState(null);
  const [addOnRequiredModalOpen, setAddOnRequiredModalOpen] = useState(false);
  const [cancelConfirmModalOpen, setCancelConfirmModalOpen] = useState(false);
  const [faqModalOpen, setFaqModalOpen] = useState(false);

  // FAQ Data
  const faqData = [
    {
      title: "Subscription Plans & Pricing",
      content: [
        {
          question: "What plans are available?",
          answer: `We offer three plans: Starter ($29 AUD / month${usdParen(29)}), Business ($75 AUD / month${usdParen(75)}), and Enterprise ($149 AUD / month${usdParen(149)}). Each plan includes different sensor capacities and features.`
        },
        {
          question: "Can I change my plan anytime?",
          answer: "Yes! You can upgrade or downgrade your plan at any time through the Customer Portal. Upgrades take effect immediately with prorated billing, while downgrades take effect at the next billing cycle."
        },
        {
          question: "How does proration work?",
          answer: "When you upgrade mid-cycle, you'll receive a credit for unused time on your current plan and be charged for the remaining time on your new plan. The total is calculated automatically and shown before you confirm."
        },
        {
          question: "What happens if I exceed my sensor limit?",
          answer: `You can add additional sensors for $5 AUD / sensor / month${usdParen(5)} through the Customer Portal. You'll be billed immediately for the additional sensors.`
        }
      ]
    },
    {
      title: "Billing & Payments",
      content: [
        {
          question: "When am I billed?",
          answer: "Subscriptions are billed monthly on the same date you originally subscribed. For example, if you subscribed on January 31st, you'll be billed on the last day of each subsequent month."
        },
        {
          question: "What payment methods are accepted?",
          answer: "We accept all major credit cards (Visa, Mastercard, American Express) through Stripe's secure payment system."
        },
        {
          question: "Can I change my payment method?",
          answer: "Yes! Click the 'Customer Portal' button on your subscription page to access the Stripe Customer Portal. There you can update payment methods, view invoices, and manage all billing details."
        },
        {
          question: "Will my price ever change?",
          answer: "Your subscription price is locked in for as long as you maintain your current plan. If you change plans, the new plan's pricing will apply. We'll notify you 30 days before any price changes."
        },
        {
          question: "What happens if a payment fails?",
          answer: "If a payment fails, we'll retry automatically over several days. Your service will remain active during this period. If payment continues to fail, your subscription may be canceled."
        }
      ]
    },
    {
      title: "Cancellations & Refunds",
      content: [
        {
          question: "How do I cancel my subscription?",
          answer: "Click the 'Cancel Subscription' button on your subscription page. You'll be redirected to the Stripe Customer Portal where you can manage your cancellation. Once canceled, you'll continue to have access until the end of your current billing period."
        },
        {
          question: "Do I get a refund if I cancel?",
          answer: "No refunds are provided for partial months. When you cancel, you'll continue to have full access until the end of your current billing period, after which your subscription will not renew."
        },
        {
          question: "Can I reactivate my subscription?",
          answer: "Yes! You can reactivate your subscription anytime. If you reactivate within your billing period, service continues uninterrupted. If you reactivate later, you'll start a new subscription."
        },
        {
          question: "What happens to my data when I cancel?",
          answer: "Your data remains safe for 90 days after cancellation. After 90 days, your account and data may be permanently deleted. You can export your data anytime before cancellation."
        }
      ]
    },
    {
      title: "Technical & Support",
      content: [
        {
          question: "How do sensors and gateways work?",
          answer: `Sensors monitor your equipment and send data through gateways to our platform. Each plan includes a set number of sensors and gateways. Additional sensors can be purchased for $5 AUD / sensor / month${usdParen(5)}.`
        },
        {
          question: "What happens during internet outages?",
          answer: "Our system includes offline functionality. Sensors continue to monitor and store data locally. When connectivity is restored, all data is automatically synced to your account."
        },
        {
          question: "How do alerts work?",
          answer: "You'll receive real-time SMS and email alerts for temperature excursions, sensor issues, or gateway connectivity problems. All plans include unlimited alerts."
        },
        {
          question: "What kind of support do I get?",
          answer: "Starter plans get email support, Business plans get priority email support, and Enterprise plans get phone support with dedicated account management."
        }
      ]
    },
    {
      title: "Account Management",
      content: [
        {
          question: "Can I have multiple locations?",
          answer: "Business and Enterprise plans support multi-site management. You can manage sensors and gateways across multiple locations from a single dashboard."
        },
        {
          question: "How do user permissions work?",
          answer: "Enterprise plans include custom user permissions. You can create roles, control access to specific features, and manage team members with different permission levels."
        },
        {
          question: "Can I export my data?",
          answer: "Yes! You can export your sensor data, reports, and account information anytime in CSV format through your dashboard."
        },
        {
          question: "How do I update my account information?",
          answer: "You can update your billing address, contact information, and other account details through the Customer Portal under 'Billing and shipping information'."
        }
      ]
    }
  ];

  const fetchSubscription = useCallback(async ({ sync = false } = {}) => {
    try {
      setLoading(true);
      const { data: { session: freshSession } } = await supabase.auth.getSession();
      const token = freshSession?.access_token || session?.access_token || user?.access_token;
      if (!token) {
        setSubscription(null);
        setLoading(false);
        return;
      }
      // Fast DB-only path by default; ?sync=1 reconciles with Stripe (used after
      // mutations like free activation / upgrade / cancel / checkout return).
      const syncFlag = sync ? '?sync=1' : '';
      const response = await fetch(`/.netlify/functions/get-subscription${syncFlag}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log('Subscription fetch response status:', response.status);
      console.log('Subscription fetch response headers:', response.headers);
      
      const contentType = response.headers.get('Content-Type') || '';
      console.log('Subscription fetch content type:', contentType);
      
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response from subscription API:', text);
        setSubscription(null);
        return;
      }
      
      const data = await response.json();
      console.log('Subscription data received:', data);
      
      if (response.ok) {
        setSubscription(data.subscription ?? null);
      } else {
        setSubscription(null);
        if (data?.error) console.error('Error fetching subscription:', data.error);
      }
    } catch (error) {
      setSubscription(null);
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, user?.access_token]);

  // Wrap fetchSubscription so the global AppDataContext stays in sync after
  // this page fetches its own (more detailed) subscription data.
  const fetchSubscriptionAndSyncGlobal = useCallback(async ({ sync = false } = {}) => {
    await fetchSubscription({ sync });
    // Propagate to the global cache so other pages see the update.
    refreshGlobalSub({ sync });
  }, [fetchSubscription, refreshGlobalSub]);

  useEffect(() => {
    if (session) {
      // sync=1 reconciles with Stripe and auto-provisions the free plan when missing.
      fetchSubscriptionAndSyncGlobal({ sync: true });
    }
  }, [fetchSubscriptionAndSyncGlobal, session]);

  // After Stripe Checkout success: backfill Supabase if webhook didn't run (sync-subscription)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const success = params.get('success') === 'true';
    if (!sessionId || !success || !session?.access_token) return;

    const token = session?.access_token || user?.access_token || (typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null);
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/.netlify/functions/sync-subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ sessionId }),
        });
        const data = res.ok ? await res.json().catch(() => ({})) : {};
        if (!cancelled && data.synced) {
          window.history.replaceState({}, '', window.location.pathname);
          fetchSubscriptionAndSyncGlobal({ sync: true });
        }
      } catch (e) {
        if (!cancelled) console.error('Sync subscription error:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [session?.access_token, user?.access_token, fetchSubscription]);

  // Handle Customer Portal upgrade returns
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const upgradeAttempted = params.get('upgrade_attempted') === 'true';
    if (!upgradeAttempted) return;

    // Clear URL parameters and refresh subscription data
    window.history.replaceState({}, '', window.location.pathname);
    
    // Wait a moment for webhook to process, then refresh
    const timer = setTimeout(() => {
      fetchSubscriptionAndSyncGlobal({ sync: true });
    }, 2000); // 2 second delay to allow webhook processing

    return () => clearTimeout(timer);
  }, [fetchSubscription]);

  // Handle Customer Portal cancellation returns
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cancellationAttempted = params.get('cancellation_attempted') === 'true';
    if (!cancellationAttempted) return;

    // Clear URL parameters and refresh subscription data
    window.history.replaceState({}, '', window.location.pathname);
    
    // Wait a moment for webhook to process, then refresh
    const timer = setTimeout(() => {
      fetchSubscriptionAndSyncGlobal({ sync: true });
      // Show success message after refresh
      setTimeout(() => {
        alert('If you canceled your subscription, it will remain active until the end of your billing period.');
      }, 1000);
    }, 2000); // 2 second delay to allow webhook processing

    return () => clearTimeout(timer);
  }, [fetchSubscription]);

  // Handle Customer Portal general returns
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const portalSession = params.get('portal_session') === 'true';
    if (!portalSession) return;

    // Clear URL parameters and refresh subscription data
    window.history.replaceState({}, '', window.location.pathname);
    
    // Wait a moment for webhook to process, then refresh
    const timer = setTimeout(() => {
      fetchSubscriptionAndSyncGlobal({ sync: true });
    }, 2000); // 2 second delay to allow webhook processing

    return () => clearTimeout(timer);
  }, [fetchSubscription]);

  const handleCancelSubscription = async () => {
    // Open confirmation modal instead of browser confirm
    setCancelConfirmModalOpen(true);
  };

  const confirmCancelSubscription = async () => {
    try {
      setActionLoading(true);
      setCancelConfirmModalOpen(false);
      const response = await fetch('/.netlify/functions/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token && { Authorization: `Bearer ${session.access_token}` }),
        },
      });
      const contentType = response.headers.get('Content-Type') || '';
      const data = contentType.includes('application/json') ? await response.json() : {};
      if (response.ok && data.ok && data.redirectUrl) {
        // Redirect to Customer Portal for cancellation
        window.location.href = data.redirectUrl;
      } else {
        alert('Error canceling subscription: ' + (data.error || response.statusText));
      }
    } catch (error) {
      if (!error.message?.includes('Unexpected token')) console.error('Error canceling subscription:', error);
      alert('Error canceling subscription. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Upgrade in place: Stripe subscription update + proration.
   * 1. Creates a new Checkout Session (stripe-create-subscription-session) for the chosen plan.
   * 2. User is redirected to Stripe Checkout to pay for the new plan.
   * 3. On success, Stripe fires checkout.session.completed; webhook/sync updates Supabase (new plan, sensors_included).
   * 4. User returns to this page; subscription is refetched and shows the upgraded plan.
   * Note: This creates a new subscription in Stripe. For in-place plan changes (proration on existing sub), you’d use stripe.subscriptions.update() with a new price ID instead of Checkout.
   */
  const handleUpgradePlan = async (newPlan) => {
    try {
      setActionLoading(true);
      const token = session?.access_token || user?.access_token || (typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null);
      const response = await fetch('/.netlify/functions/upgrade-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ newPlan }),
      });
      const contentType = response.headers.get('Content-Type') || '';
      const data = contentType.includes('application/json') ? await response.json() : {};
      if (response.ok && data.ok) {
        setUpgradeModalOpen(false);
        if (data.redirectUrl) {
          window.location.href = data.redirectUrl;
          return;
        }
        await fetchSubscriptionAndSyncGlobal({ sync: true });
        alert(data.message || 'Upgrade applied. Your plan has been updated.');
      } else {
        const msg = [data.error, data.details].filter(Boolean).join(' — ');
        alert('Error upgrading: ' + (msg || response.statusText));
      }
    } catch (error) {
      if (!error.message?.includes('Unexpected token')) console.error('Error upgrading:', error);
      alert('Error upgrading. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddSensors = async () => {
    if (!subscription || sensorQuantity < 1) {
      alert('Please select a valid number of sensors');
      return;
    }
    try {
      setActionLoading(true);
      const response = await fetch('/.netlify/functions/additional-sensors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token && { Authorization: `Bearer ${session.access_token}` }),
        },
        body: JSON.stringify({ quantity: sensorQuantity, userId: user.id }),
      });
      const contentType = response.headers.get('Content-Type') || '';
      const data = contentType.includes('application/json') ? await response.json() : {};
      const redirectUrl = data.session?.url ?? data.sessionUrl;
      if (response.ok && redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        const msg = [data.error, data.details, response.statusText].filter(Boolean).join(' — ');
        alert('Error purchasing additional sensors: ' + (msg || 'Unknown error'));
      }
    } catch (error) {
      if (!error.message?.includes('Unexpected token')) console.error('Error purchasing additional sensors:', error);
      alert('Error purchasing additional sensors. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFixBillingPeriod = async () => {
    if (!window.confirm('This will fix your billing period dates. Continue?')) {
      return;
    }
    try {
      setActionLoading(true);
      const token = session?.access_token || user?.access_token || (typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null);
      const response = await fetch('/.netlify/functions/fix-billing-period', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      const data = await response.json();
      if (response.ok) {
        alert('Billing period fixed successfully!');
        fetchSubscriptionAndSyncGlobal({ sync: true }); // Refresh the data
      } else {
        alert('Error fixing billing period: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error fixing billing period:', error);
      alert('Error fixing billing period. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCustomerPortal = async () => {
    try {
      setActionLoading(true);
      const token = session?.access_token || user?.access_token || (typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null);
      const response = await fetch('/.netlify/functions/customer-portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      const contentType = response.headers.get('Content-Type') || '';
      const data = contentType.includes('application/json') ? await response.json() : {};
      if (response.ok && data.ok && data.redirectUrl) {
        // Redirect to Customer Portal
        window.location.href = data.redirectUrl;
      } else {
        alert('Error accessing Customer Portal: ' + (data.error || response.statusText));
      }
    } catch (error) {
      if (!error.message?.includes('Unexpected token')) console.error('Error accessing Customer Portal:', error);
      alert('Error accessing Customer Portal. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return successColor;
      case 'canceled': return errorColor;
      case 'past_due': return warningColor;
      case 'unpaid': return errorColor;
      case 'incomplete': return warningColor;
      default: return textSecondary;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return Checkmark;
      case 'canceled': return Error;
      case 'past_due': return Information;
      case 'unpaid': return Error;
      case 'incomplete': return Information;
      default: return Information;
    }
  };

  const formatDate = (timestamp) => {
    if (timestamp == null || timestamp === '') return '—';
    
    let date;
    if (typeof timestamp === 'number') {
      // Handle Unix timestamp (seconds) - convert to milliseconds
      date = new Date(timestamp * 1000);
    } else if (typeof timestamp === 'string') {
      // Handle ISO string format from database
      date = new Date(timestamp);
    } else {
      // Handle any other type (fallback)
      date = new Date(timestamp);
    }
    
    if (Number.isNaN(date.getTime())) {
      console.error('Invalid date format:', timestamp);
      return '—';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '64px', 
        textAlign: 'center',
        backgroundColor: bgPrimary,
        color: textPrimary,
        minHeight: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Loading />
      </div>
    );
  }

  return (
    <div className="subscription-page-wrapper" style={{ 
      padding: '64px 20px',
      backgroundColor: bgPrimary,
      color: textPrimary,
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      boxSizing: 'border-box',
    }}>
      <style>{`
        .subscription-page-wrapper,
        .subscription-page-wrapper * {
          box-sizing: border-box;
        }
        .subscription-page-wrapper .cds--tile {
          min-width: 0;
          max-width: 100%;
        }
        .subscription-page-wrapper .cds--btn {
          max-width: 100%;
          white-space: normal;
          height: auto;
          min-height: 2.5rem;
        }
        .pricing-tiers-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
          margin-bottom: 48px;
          align-items: stretch;
          width: 100%;
        }
        .subscription-billing-toggle {
          flex-wrap: wrap;
          row-gap: 12px;
          justify-content: center;
        }
        .subscription-addon-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          gap: 16px;
          width: 100%;
        }
        .pricing-tier-card {
          min-width: 0 !important;
          max-width: 100% !important;
          overflow: hidden;
        }
        .pricing-tier-price span {
          overflow-wrap: anywhere;
        }
        @media (min-width: 672px) {
          .pricing-tiers-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .subscription-addon-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (min-width: 1056px) {
          .pricing-tiers-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); margin-bottom: 64px; }
        }
        @media (max-width: 1055px) {
          .subscription-layout { grid-template-columns: 1fr !important; gap: 24px !important; }
        }
        @media (max-width: 672px) {
          .subscription-page-wrapper { padding: 24px 16px !important; }
          .subscription-page-wrapper .subscription-page-title { font-size: 1.75rem !important; }
          .subscription-current-tile { padding: 20px 16px !important; }
          .subscription-usage-tile,
          .subscription-addon-tile,
          .subscription-need-more-tile,
          .subscription-quick-tile { padding: 20px 16px !important; }
          .subscription-usage-tile table th,
          .subscription-usage-tile table td { padding: 8px 6px !important; font-size: 0.75rem; }
          .subscription-need-more-tile h2 { font-size: 1.25rem !important; }
          .subscription-actions { flex-direction: column; width: 100%; }
          .subscription-actions .cds--btn { width: 100%; justify-content: center; min-width: 0; max-width: 100%; }
          .subscription-no-plan-title { font-size: 1.5rem !important; }
          .subscription-no-plan-copy { font-size: 1rem !important; padding: 0 4px; }
          .subscription-lock-icon { width: 48px; height: 48px; }
          .pricing-tier-card { padding: 20px 14px !important; }
          .pricing-tier-price { font-size: 1.75rem !important; }
          .subscription-current-price { font-size: 1.5rem !important; overflow-wrap: anywhere; }
          .subscription-page-wrapper .cds--btn { width: 100%; justify-content: center; }
          .pricing-tier-card:hover { transform: none !important; }
        }
        @media (max-width: 480px) {
          .subscription-page-wrapper { padding: 16px 12px !important; }
          .subscription-page-wrapper .subscription-page-title { font-size: 1.375rem !important; }
          .pricing-tier-price { font-size: 1.5rem !important; }
          .pricing-tier-price > span:not(:first-child) { font-size: 0.95rem !important; }
        }
      `}</style>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px', flexWrap: 'wrap', minWidth: 0 }}>
          <h1 className="subscription-page-title" style={{ fontSize: '2.5rem', fontWeight: '600', margin: 0, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
            Your Subscription
          </h1>
          <Tooltip
            label="Manage your ChillVerify subscription plan, view billing details, upgrade or downgrade your plan, and access your subscription history and payment information."
            align="bottom"
            alignOffset={4}
          >
            <button
              style={{
                background: 'none',
                border: 'none',
                padding: '4px',
                borderRadius: '50%',
                cursor: 'pointer',
                color: textSecondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label="Subscription help"
            >
              <Information size={20} />
            </button>
          </Tooltip>
        </div>

        {!subscription ? (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginBottom: '32px' 
              }}>
                <Locked className="subscription-lock-icon" size={64} style={{ color: '#0f62fe' }} />
              </div>
              <h3 className="subscription-no-plan-title" style={{ fontSize: '2rem', fontWeight: '600', marginBottom: '16px' }}>
                No Active Subscription
              </h3>
              <p className="subscription-no-plan-copy" style={{ fontSize: '1.125rem', color: textSecondary, maxWidth: '600px', margin: '0 auto', marginBottom: '32px' }}>
                You don't have an active ChillVerify subscription. Choose a plan below to get started with monitoring your cold chain.
              </p>
            </div>

            {/* Billing Interval Toggle */}
            <div className="subscription-billing-toggle" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginBottom: '48px' }}>
              <span style={{
                fontSize: '1rem',
                fontWeight: billingInterval === 'monthly' ? 600 : 400,
                color: billingInterval === 'monthly' ? textPrimary : textSecondary,
                cursor: 'pointer',
              }} onClick={() => setBillingInterval('monthly')}>
                Monthly
              </span>
              <button
                onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')}
                style={{
                  width: '56px',
                  height: '28px',
                  borderRadius: '14px',
                  border: 'none',
                  backgroundColor: billingInterval === 'yearly' ? '#0f62fe' : (isDark ? '#525252' : '#c6c6c6'),
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  padding: 0,
                }}
                aria-label="Toggle billing interval"
              >
                <div style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  position: 'absolute',
                  top: '3px',
                  left: billingInterval === 'yearly' ? '31px' : '3px',
                  transition: 'left 0.2s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </button>
              <span style={{
                fontSize: '1rem',
                fontWeight: billingInterval === 'yearly' ? 600 : 400,
                color: billingInterval === 'yearly' ? textPrimary : textSecondary,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }} onClick={() => setBillingInterval('yearly')}>
                Yearly
                <span style={{
                  backgroundColor: '#24a148',
                  color: '#ffffff',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: '12px',
                }}>
                  Save 20%
                </span>
              </span>
            </div>

            <div className="pricing-tiers-grid">
              {Object.values(pricingTiers).map((tier) => (
                <PricingTier key={tier.name} tier={tier} />
              ))}
            </div>

            {/* Usage-Based Pricing */}
            <Tile className="subscription-need-more-tile" style={{ padding: '48px', textAlign: 'center' }}>
              <h2 style={{ fontSize: '1.75rem', fontWeight: '600', marginBottom: '16px' }}>
                Need More Than Your Plan?
              </h2>
              <p style={{ color: textSecondary, marginBottom: '32px', maxWidth: '600px', margin: '0 auto 32px' }}>
                Add additional sensors and gateway connectivity to scale your monitoring system as needed.
              </p>
              <div className="subscription-addon-grid" style={{ marginBottom: '32px' }}>
                <div style={{ 
                  backgroundColor: bgSecondary, 
                  padding: '24px', 
                  borderRadius: '12px',
                  border: `1px solid ${borderColor}`
                }}>
                  <h3 style={{ color: textPrimary, marginBottom: '12px' }}>Additional Sensors</h3>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0f62fe', marginBottom: '8px' }}>
                    $5 AUD / sensor / month{usdParen(5)}
                  </div>
                  <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '16px' }}>
                    Per additional sensor beyond your plan limit
                  </p>
                  <Button 
                    kind="primary" 
                    size="sm"
                    onClick={() => setAddOnRequiredModalOpen(true)}
                  >
                    Add Sensors
                  </Button>
                </div>
                <div style={{ 
                  backgroundColor: bgSecondary, 
                  padding: '24px', 
                  borderRadius: '12px',
                  border: `1px solid ${borderColor}`
                }}>
                  <h3 style={{ color: textPrimary, marginBottom: '12px' }}>Cellular Connectivity</h3>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0f62fe', marginBottom: '8px' }}>
                    $15 AUD / gateway / month{usdParen(15)}
                  </div>
                  <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '16px' }}>
                    4G/LTE data for gateways with cellular backup
                  </p>
                  <Button 
                    kind="primary" 
                    size="sm"
                    onClick={() => setAddOnRequiredModalOpen(true)}
                  >
                    Add Connectivity
                  </Button>
                </div>
              </div>
              <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
                <strong>Note:</strong> Usage-based add-ons require an active subscription. You'll be able to add these after selecting a plan above.
              </p>
            </Tile>
          </div>
        ) : (
          <div className="subscription-layout" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '32px' }}>
            {/* Current Subscription */}
            <Tile className="subscription-current-tile" style={{ padding: '32px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '8px' }}>
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Current Plan</h2>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  color: getStatusColor(subscription.status)
                }}>
                  {React.createElement(getStatusIcon(subscription.status), { size: 20 })}
                  <span style={{ textTransform: 'capitalize' }}>{subscription.status}</span>
                </div>
              </div>

              {(subscription.status === 'past_due' || subscription.status === 'unpaid') && (
                <div style={{ marginBottom: '20px' }}>
                  <InlineNotification
                    kind="warning"
                    title="Payment required"
                    subtitle="Your ChillVerify subscription needs attention. Open Customer Portal to update your payment method and keep your account in good standing."
                    lowContrast
                    hideCloseButton
                  />
                </div>
              )}

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '8px' }}>
                  {subscription.planName}
                </h3>
                <div className="subscription-current-price" style={{ fontSize: '2rem', fontWeight: '700', color: '#0f62fe', marginBottom: '8px', lineHeight: 1.2, overflowWrap: 'anywhere' }}>
                  {Number(subscription.monthlyPrice) === 0
                    ? 'Free'
                    : `$${subscription.monthlyPrice} AUD / month`}
                </div>
                <div style={{ color: textSecondary, marginBottom: '6px' }}>
                  {subscription.sensorsIncluded} sensors included
                </div>
                {typeof subscription.additionalSensorsQuantity === 'number' && subscription.additionalSensorsQuantity > 0 && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      backgroundColor: isDark ? 'rgba(52,199,89,0.15)' : 'rgba(52,199,89,0.12)',
                      border: '1px solid rgba(52,199,89,0.5)',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      color: '#24a148',
                    }}
                  >
                    <span style={{ marginRight: '6px' }}>Additional sensors</span>
                    <span style={{ fontWeight: 600 }}>+{subscription.additionalSensorsQuantity}</span>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px' }}>Billing Period</h4>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ color: textSecondary, flexShrink: 0 }}>Current Period:</span>
                    <span style={{ textAlign: 'right', wordBreak: 'break-word' }}>{formatDate(subscription.currentPeriodStart)} – {formatDate(subscription.currentPeriodEnd)}</span>
                  </div>
                  {subscription.cancelAtPeriodEnd && (
                    <div style={{ 
                      color: warningColor,
                      fontWeight: '500',
                      textAlign: 'center',
                      padding: '12px',
                      backgroundColor: isDark ? '#2d1b00' : '#fff8e1',
                      borderRadius: '8px',
                      border: `1px solid ${warningColor}`
                    }}>
                      Cancels on {formatDate(subscription.currentPeriodEnd)}
                    </div>
                  )}
                </div>
              </div>

              <div className="subscription-actions" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                {!subscription.cancelAtPeriodEnd && subscription.status === 'active' && (
                  <>
                    <Button
                      kind="primary"
                      size="sm"
                      onClick={() => setUpgradeModalOpen(true)}
                      disabled={actionLoading}
                      renderIcon={ArrowUp}
                    >
                      Upgrade
                    </Button>
                    {/* Cancel only makes sense for paid plans — free is already $0. */}
                    {subscription.plan !== 'free' && (
                      <Button
                        kind="danger"
                        size="sm"
                        onClick={handleCancelSubscription}
                        disabled={actionLoading}
                        renderIcon={actionLoading ? null : Error}
                      >
                        {actionLoading ? <InlineLoading /> : 'Cancel Subscription'}
                      </Button>
                    )}
                  </>
                )}
                {/* Show fix button if billing period dates are the same */}
                {subscription.current_period_start === subscription.current_period_end && (
                  <Button
                    kind="secondary"
                    size="sm"
                    onClick={handleFixBillingPeriod}
                    disabled={actionLoading}
                    renderIcon={actionLoading ? null : undefined}
                  >
                    {actionLoading ? <InlineLoading /> : 'Fix Billing Period'}
                  </Button>
                )}
                <Button
                  kind={(subscription.status === 'past_due' || subscription.status === 'unpaid') ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={handleCustomerPortal}
                  disabled={actionLoading}
                  renderIcon={actionLoading ? null : undefined}
                >
                  {actionLoading ? <InlineLoading /> : 'Customer Portal'}
                </Button>
              </div>
            </Tile>

            {/* Quick Actions */}
            <div>
              <Tile className="subscription-quick-tile" style={{ padding: '24px', marginBottom: '24px' }}>
                <h3 style={{ marginBottom: '16px' }}>Quick Actions</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Button 
                    kind="secondary" 
                    size="sm"
                    onClick={() => window.location.href = '/billing'}
                    renderIcon={Renew}
                  >
                    Billing History
                  </Button>
                </div>
              </Tile>

              <Tile className="subscription-quick-tile" style={{ padding: '24px' }}>
                <h3 style={{ marginBottom: '16px' }}>Need Help?</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Button 
                    kind="ghost" 
                    size="sm"
                    onClick={() => window.location.href = '/support'}
                  >
                    Contact Support
                  </Button>
                  <Button 
                    kind="ghost" 
                    size="sm"
                    onClick={() => setFaqModalOpen(true)}
                    renderIcon={Information}
                  >
                    Subscription FAQ
                  </Button>
                </div>
              </Tile>
            </div>
          </div>
        )}

        {/* Usage Stats */}
        {subscription && (
          <Tile className="subscription-usage-tile" style={{ marginTop: '32px', padding: '32px' }}>
            <h2 style={{ marginBottom: '24px' }}>Usage Overview</h2>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  backgroundColor: bgSecondary,
                  border: `1px solid ${borderColor}`,
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: isDark ? '#353535' : '#e0e0e0' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem' }}>Resource</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', color: textPrimary, fontWeight: 600, fontSize: '0.875rem' }}>Used</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', color: textPrimary, fontWeight: 600, fontSize: '0.875rem' }}>Included</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', color: textPrimary, fontWeight: 600, fontSize: '0.875rem' }}>Available</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const sensorsUsed = subscription.sensorsUsed ?? 0;
                    const sensorsIncluded = subscription.sensorsIncluded ?? 0;
                    const sensorsUsedOnIncluded = subscription.sensorsUsedOnIncluded ?? Math.min(sensorsUsed, sensorsIncluded);
                    const sensorsAvailable = subscription.sensorsAvailableOnIncluded ?? Math.max(0, sensorsIncluded - sensorsUsedOnIncluded);
                    const gatewaysUsed = subscription.gatewaysUsed ?? 0;
                    const gatewaysIncluded = subscription.gatewaysIncluded ?? 0;
                    const gatewaysAvailable = Math.max(0, gatewaysIncluded - gatewaysUsed);
                    const additionalSensorCapacity = subscription.additionalSensorsQuantity ?? 0;
                    const additionalSensorsUsed = subscription.sensorsUsedOnAddons ?? Math.min(
                      Math.max(0, sensorsUsed - sensorsIncluded),
                      additionalSensorCapacity
                    );
                    const additionalSensorsAvailable = subscription.sensorsAvailableOnAddons ?? Math.max(
                      0,
                      additionalSensorCapacity - additionalSensorsUsed
                    );
                    return (
                      <>
                        <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                          <td style={{ padding: '12px 16px', color: textPrimary }}>Sensors</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', color: textSecondary }}>{sensorsUsedOnIncluded}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', color: textSecondary }}>{sensorsIncluded}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', color: successColor, fontWeight: 600 }}>{sensorsAvailable}</td>
                        </tr>
                        <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                          <td style={{ padding: '12px 16px', color: textPrimary }}>Gateways</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', color: textSecondary }}>{gatewaysUsed}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', color: textSecondary }}>{gatewaysIncluded}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', color: successColor, fontWeight: 600 }}>{gatewaysAvailable}</td>
                        </tr>
                        {additionalSensorCapacity > 0 && (
                          <tr>
                            <td style={{ padding: '12px 16px', color: textPrimary }}>Additional sensors (add‑ons)</td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: textSecondary }}>{additionalSensorsUsed}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: textSecondary }}>{additionalSensorCapacity}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'right', color: successColor, fontWeight: 600 }}>{additionalSensorsAvailable}</td>
                          </tr>
                        )}
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </Tile>
        )}

        {/* Additional Sensors */}
        {subscription && (
          <Tile className="subscription-addon-tile" style={{ marginTop: '32px', padding: '32px' }}>
            <h2 style={{ marginBottom: '24px' }}>Add More Sensors</h2>
            <div style={{ marginBottom: '24px' }}>
              <p style={{ color: textSecondary, marginBottom: '16px' }}>
                Need more sensors? Add additional sensors to your plan for $5 AUD / sensor / month{usdParen(5)}.
              </p>
              <div className="subscription-addon-grid" style={{ alignItems: 'center' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Number of Additional Sensors
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      onClick={() => setSensorQuantity(Math.max(1, sensorQuantity - 1))}
                      style={{
                        width: '36px',
                        height: '36px',
                        border: `1px solid ${borderColor}`,
                        backgroundColor: bgPrimary,
                        color: textPrimary,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '18px'
                      }}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={sensorQuantity}
                      onChange={(e) => setSensorQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{
                        width: '80px',
                        padding: '8px',
                        textAlign: 'center',
                        border: `1px solid ${borderColor}`,
                        backgroundColor: bgPrimary,
                        color: textPrimary,
                        borderRadius: '6px',
                        fontSize: '16px'
                      }}
                    />
                    <button
                      onClick={() => setSensorQuantity(Math.min(50, sensorQuantity + 1))}
                      style={{
                        width: '36px',
                        height: '36px',
                        border: `1px solid ${borderColor}`,
                        backgroundColor: bgPrimary,
                        color: textPrimary,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '18px'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '8px' }}>
                    ${(sensorQuantity * 5).toFixed(2)} AUD / month{usdParen(sensorQuantity * 5)}
                  </div>
                  <div style={{ color: textSecondary, fontSize: '0.875rem' }}>
                    $5 AUD / sensor / month{usdParen(5)}
                  </div>
                </div>
              </div>
            </div>
            <Button
              kind="primary"
              size="lg"
              onClick={handleAddSensors}
              disabled={actionLoading}
              renderIcon={actionLoading ? null : undefined}
              style={{ width: '100%', maxWidth: '100%', whiteSpace: 'normal' }}
            >
              {actionLoading ? <InlineLoading /> : `Add ${sensorQuantity} Sensor${sensorQuantity > 1 ? 's' : ''}`}
            </Button>
          </Tile>
        )}

        {/* Modal: Upgrade plan */}
        <Modal
          open={upgradeModalOpen}
          modalHeading="Upgrade plan"
          passiveModal
          onRequestClose={() => setUpgradeModalOpen(false)}
          size="sm"
        >
          <p style={{ marginBottom: '16px', color: textSecondary }}>
            You will be taken to Stripe to pay the prorated difference. You pay the new price; Stripe prorates so you’re charged only for the remainder of your billing period. After payment, your plan and sensor limit update immediately.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(() => {
              const planOrder = ['starter', 'business', 'enterprise'];
              const currentKey = (subscription?.plan && planOrder.includes(subscription.plan.toLowerCase()) ? subscription.plan.toLowerCase() : null)
                || planOrder.find(k => pricingTiers[k]?.name === subscription?.planName) || 'starter';
              const currentIndex = planOrder.indexOf(currentKey);
              const upgradePlans = planOrder.filter((_, i) => { return i > currentIndex; }).map(k => ({ key: k, ...pricingTiers[k] }));
              if (upgradePlans.length === 0) {
                return <p style={{ margin: 0, color: textSecondary }}>You’re on the top plan (Enterprise).</p>;
              }
              return upgradePlans.map(({ key, name, basePrice }) => (
                <Button
                  key={key}
                  kind="primary"
                  size="sm"
                  onClick={() => {
                    setUpgradeModalOpen(false);
                    handleUpgradePlan(key);
                  }}
                  disabled={actionLoading}
                >
                  Upgrade to {name} — ${basePrice}/month
                </Button>
              ));
            })()}
          </div>
        </Modal>

        {/* Modal: active subscription required for add-ons */}
        <Modal
          open={addOnRequiredModalOpen}
          danger={false}
          modalHeading="Subscription Required"
          modalLabel="Add-on Purchase"
          primaryButtonText="OK"
          onRequestClose={() => setAddOnRequiredModalOpen(false)}
          onRequestSubmit={() => setAddOnRequiredModalOpen(false)}
        >
          <p style={{ margin: 0 }}>
            You need to have an active subscription first before you add additional sensors or gateway connectivity. Choose a plan above to get started.
          </p>
        </Modal>

        {/* Cancellation Confirmation Modal */}
        <Modal
          open={cancelConfirmModalOpen}
          danger
          modalHeading="Cancel Subscription"
          modalLabel="Confirm Cancellation"
          primaryButtonText="Cancel Subscription"
          secondaryButtonText="Keep Subscription"
          onRequestClose={() => setCancelConfirmModalOpen(false)}
          onRequestSubmit={confirmCancelSubscription}
          onSecondarySubmit={() => setCancelConfirmModalOpen(false)}
        >
          <div style={{ marginBottom: '16px' }}>
            <p style={{ margin: '0 0 16px 0', fontWeight: '500', color: textPrimary }}>
              Are you sure you want to cancel your subscription?
            </p>
            <p style={{ margin: 0, color: textSecondary, lineHeight: '1.6' }}>
              You will lose access to ChillVerify features at the end of your billing period. 
              Your subscription will remain active until {subscription?.current_period_end ? 
                new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                  year: 'numeric', month: 'long', day: 'numeric'
                }) : 'the end of your billing period'
              }.
            </p>
          </div>
          <div style={{ 
            padding: '12px', 
            backgroundColor: isDark ? '#2d1b1b' : '#fef2f2', 
            border: `1px solid ${isDark ? '#5c1a1a' : '#fecaca'}`,
            borderRadius: '6px'
          }}>
            <p style={{ margin: 0, fontSize: '0.875rem', color: isDark ? '#fca5a5' : '#dc2626' }}>
              <strong>Important:</strong> Once canceled, you can reactivate anytime, but you may lose any promotional pricing.
            </p>
          </div>
        </Modal>

        {/* FAQ Modal as Side Panel */}
        <ComposedModal
          open={faqModalOpen}
          onClose={() => setFaqModalOpen(false)}
          size="lg"
        >
          <ModalHeader title="Subscription FAQ" subtitle="Help & Support" />
          <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', padding: '16px' }}>
            <Accordion>
              {faqData.map((category, categoryIndex) => (
                <AccordionItem 
                  key={categoryIndex} 
                  title={
                    <div style={{ 
                      fontSize: '1.1rem', 
                      fontWeight: '600',
                      color: textPrimary 
                    }}>
                      {category.title}
                    </div>
                  }
                >
                  <div style={{ padding: '16px 0' }}>
                    {category.content.map((item, itemIndex) => (
                      <div 
                        key={itemIndex} 
                        style={{ 
                          marginBottom: itemIndex < category.content.length - 1 ? '24px' : '0',
                          paddingBottom: itemIndex < category.content.length - 1 ? '24px' : '0',
                          borderBottom: itemIndex < category.content.length - 1 ? `1px solid ${borderColor}` : 'none'
                        }}
                      >
                        <h4 style={{ 
                          margin: '0 0 8px 0', 
                          color: textPrimary,
                          fontSize: '1rem',
                          fontWeight: '600'
                        }}>
                          {item.question}
                        </h4>
                        <p style={{ 
                          margin: 0, 
                          color: textSecondary,
                          lineHeight: '1.6'
                        }}>
                          {item.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </AccordionItem>
              ))}
            </Accordion>
          </ModalBody>
          <ModalFooter>
            <Button kind="secondary" onClick={() => setFaqModalOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </ComposedModal>
      </div>
    </div>
  );
};

export default SubscriptionPage;
